<template>
  <v-card class="mongodb-card" elevation="4">
    <v-card-title>
      <h2 class="section-title">
        <v-icon left small>mdi-database</v-icon>
        MongoDB Status
      </h2>
      <v-spacer></v-spacer>
      <v-chip 
        :color="getStatusColor()" 
        class="status-indicator"
        small
      >
        <v-icon left small>{{ getStatusIcon() }}</v-icon>
        {{ getStatusText() }}
      </v-chip>
    </v-card-title>
    <v-card-text>
      <!-- Connection Info -->
      <v-row class="mb-3">
        <v-col cols="12">
          <v-alert
            :type="mongodb.connected ? 'success' : 'error'"
            dense
            outlined
            class="mb-0"
          >
            <div class="d-flex align-center justify-space-between">
              <span>{{ mongodb.connected ? 'Verbunden' : 'Getrennt' }}</span>
              <span class="caption">{{ mongodb.host || 'localhost:27017' }}</span>
            </div>
          </v-alert>
        </v-col>
      </v-row>
      
      <!-- Database Stats -->
      <v-row>
        <v-col v-for="stat in dbStats" :key="stat.label" cols="6" sm="3">
          <v-card
            :color="stat.color"
            dark
            class="stat-card"
            elevation="0"
          >
            <v-card-text class="pa-3 text-center">
              <v-icon class="mb-1">{{ stat.icon }}</v-icon>
              <div class="stat-value">{{ formatNumber(stat.value) }}</div>
              <div class="stat-label">{{ stat.label }}</div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      
      <!-- Collections Info -->
      <div class="mt-4">
        <h3 class="metric-title mb-3">Collections</h3>
        <v-data-table
          :headers="collectionHeaders"
          :items="collections"
          :items-per-page="5"
          dense
          dark
          class="collections-table"
        >
          <template v-slot:item.size="{ item }">
            {{ formatSize(item.size) }}
          </template>
          <template v-slot:item.documents="{ item }">
            {{ formatNumber(item.documents) }}
          </template>
          <template v-slot:item.indexes="{ item }">
            <v-chip small color="primary">{{ item.indexes }}</v-chip>
          </template>
          <template v-slot:item.avgObjSize="{ item }">
            {{ formatSize(item.avgObjSize) }}
          </template>
        </v-data-table>
      </div>
      
      <!-- Performance Metrics -->
      <div class="mt-4">
        <h3 class="metric-title mb-3">Performance</h3>
        <v-row>
          <v-col cols="12" md="6">
            <apexchart
              v-if="performanceChartSeries && performanceChartSeries[0] && performanceChartSeries[0].data.length > 0"
              type="area"
              height="180"
              :options="performanceChartOptions"
              :series="performanceChartSeries"
            ></apexchart>
            <div v-else class="text-center py-4">
              <v-icon size="48" color="grey">mdi-chart-line</v-icon>
              <div class="caption grey--text mt-2">Keine Performance-Daten verfügbar</div>
            </div>
          </v-col>
          <v-col cols="12" md="6">
            <v-list dense dark class="operations-list">
              <v-list-item
                v-for="(op, index) in recentOperationsLimited"
                :key="index"
                class="operation-item"
              >
                <v-list-item-avatar size="28">
                  <v-icon 
                    :color="getOperationColor(op.type)"
                    small
                  >
                    {{ getOperationIcon(op.type) }}
                  </v-icon>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title class="caption">
                    {{ op.collection }} - {{ op.type }}
                  </v-list-item-title>
                  <v-list-item-subtitle class="caption">
                    {{ op.duration }}ms - {{ formatTime(op.timestamp) }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
              <v-list-item v-if="!recentOperations || recentOperations.length === 0">
                <v-list-item-content>
                  <div class="text-center caption grey--text">
                    Keine aktuellen Operationen
                  </div>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-col>
        </v-row>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import VueApexCharts from 'vue-apexcharts';

export default {
  name: 'MongoDBCard',
  
  components: {
    apexchart: VueApexCharts,
  },
  
  props: {
    mongodb: {
      type: Object,
      default: () => ({
        connected: false,
        host: 'localhost:27017',
        database: 'backoffice',
        collections: 0,
        documents: 0,
        size: 0,
        indexes: 0
      })
    },
    collections: {
      type: Array,
      default: () => []
    },
    recentOperations: {
      type: Array,
      default: () => []
    },
    performanceData: {
      type: Array,
      default: () => []
    }
  },
  
  data() {
    return {
      collectionHeaders: [
        { text: 'Collection', value: 'name' },
        { text: 'Documents', value: 'documents', align: 'end' },
        { text: 'Size', value: 'size', align: 'end' },
        { text: 'Indexes', value: 'indexes', align: 'center' },
        { text: 'Avg Size', value: 'avgObjSize', align: 'end' }
      ],
      
      performanceChartOptions: {
        chart: {
          type: 'area',
          background: 'transparent',
          toolbar: { show: false },
          animations: {
            enabled: true,
            easing: 'linear',
            dynamicAnimation: {
              speed: 1000
            }
          }
        },
        theme: {
          mode: 'dark'
        },
        stroke: {
          curve: 'smooth',
          width: 2
        },
        fill: {
          type: 'gradient',
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.7,
            opacityTo: 0.2
          }
        },
        xaxis: {
          type: 'datetime',
          labels: {
            style: { colors: '#fff' },
            datetimeFormatter: {
              hour: 'HH:mm'
            }
          }
        },
        yaxis: {
          title: {
            text: 'Operations/s',
            style: { color: '#fff' }
          },
          labels: {
            style: { colors: '#fff' }
          }
        },
        colors: ['#00D9FF', '#00E396'],
        legend: {
          position: 'top',
          labels: { colors: '#fff' }
        },
        grid: {
          borderColor: 'rgba(255, 255, 255, 0.1)'
        },
        tooltip: {
          theme: 'dark',
          x: { format: 'HH:mm:ss' }
        }
      },
      performanceChartSeries: [
        {
          name: 'Reads/s',
          data: []
        },
        {
          name: 'Writes/s',
          data: []
        }
      ]
    };
  },
  
  computed: {
    dbStats() {
      // Handle both old and new data structures
      const connections = this.mongodb.connections || {};
      return [
        {
          label: 'Current Connections',
          value: connections.current || 0,
          icon: 'mdi-connection',
          color: 'blue darken-3'
        },
        {
          label: 'Active',
          value: connections.active || 0,
          icon: 'mdi-pulse',
          color: 'green darken-3'
        },
        {
          label: 'Available',
          value: connections.available || 0,
          icon: 'mdi-database-check',
          color: 'orange darken-3'
        },
        {
          label: 'Version',
          value: this.mongodb.version || 'N/A',
          icon: 'mdi-tag',
          color: 'purple darken-3'
        }
      ];
    },
    
    recentOperationsLimited() {
      if (!this.recentOperations || !Array.isArray(this.recentOperations)) {
        return [];
      }
      return this.recentOperations.slice(0, 5);
    }
  },
  
  watch: {
    performanceData: {
      immediate: true,
      deep: true,
      handler(newData) {
        this.updatePerformanceChart(newData);
      }
    }
  },
  
  methods: {
    getStatusColor() {
      if (!this.mongodb.connected) return 'error';
      if (this.mongodb.slowQueries > 10) return 'warning';
      return 'success';
    },
    
    getStatusIcon() {
      if (!this.mongodb.connected) return 'mdi-alert-circle';
      if (this.mongodb.slowQueries > 10) return 'mdi-alert';
      return 'mdi-check-circle';
    },
    
    getStatusText() {
      if (!this.mongodb.connected) return 'Disconnected';
      if (this.mongodb.slowQueries > 10) return 'Slow Queries';
      return 'Healthy';
    },
    
    formatNumber(num) {
      if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
      if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
      return num.toString();
    },
    
    formatSize(bytes) {
      if (!bytes) return '0 B';
      const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
      const i = Math.floor(Math.log(bytes) / Math.log(1024));
      return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
    },
    
    formatTime(timestamp) {
      if (!timestamp) return '';
      const date = new Date(timestamp);
      const now = new Date();
      const diff = now - date;
      
      if (diff < 1000) return 'now';
      if (diff < 60000) return Math.floor(diff / 1000) + 's ago';
      if (diff < 3600000) return Math.floor(diff / 60000) + 'm ago';
      return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
    },
    
    getOperationColor(type) {
      const colors = {
        'find': 'blue',
        'insert': 'green',
        'update': 'orange',
        'delete': 'red',
        'aggregate': 'purple',
        'index': 'cyan'
      };
      return colors[type] || 'grey';
    },
    
    getOperationIcon(type) {
      const icons = {
        'find': 'mdi-magnify',
        'insert': 'mdi-plus',
        'update': 'mdi-pencil',
        'delete': 'mdi-delete',
        'aggregate': 'mdi-sigma',
        'index': 'mdi-bookmark'
      };
      return icons[type] || 'mdi-database';
    },
    
    updatePerformanceChart(data) {
      if (!data || data.length === 0) {
        this.performanceChartSeries = [
          { name: 'Reads/s', data: [] },
          { name: 'Writes/s', data: [] }
        ];
        return;
      }
      
      const reads = data.map(point => ({
        x: new Date(point.timestamp).getTime(),
        y: point.reads || 0
      }));
      
      const writes = data.map(point => ({
        x: new Date(point.timestamp).getTime(),
        y: point.writes || 0
      }));
      
      this.performanceChartSeries = [
        { name: 'Reads/s', data: reads },
        { name: 'Writes/s', data: writes }
      ];
    }
  }
};
</script>

<style scoped>
.mongodb-card {
  background: linear-gradient(135deg, rgba(30, 30, 30, 0.95) 0%, rgba(20, 20, 20, 0.95) 100%);
}

.section-title {
  font-size: 1.2rem;
  font-weight: 500;
}

.status-indicator {
  font-weight: bold;
  text-transform: uppercase;
}

.stat-card {
  border-radius: 8px;
  transition: all 0.3s ease;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
}

.stat-value {
  font-size: 1.5rem;
  font-weight: bold;
  color: #fff;
}

.stat-label {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.7);
  text-transform: uppercase;
}

.metric-title {
  font-size: 1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
}

.collections-table {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 4px;
}

.operations-list {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 4px;
  max-height: 180px;
  overflow-y: auto;
}

.operation-item {
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.operation-item:last-child {
  border-bottom: none;
}

:deep(.v-data-table) {
  background: transparent !important;
}

:deep(.v-data-table__wrapper) {
  background: rgba(0, 0, 0, 0.1);
  border-radius: 4px;
}
</style>